import {Observable, Subject, Subscription} from 'rxjs';

import IErrorsPayload            from '../../../Player/Interfaces/IErrorsPayload';
import logger                    from '../../../Shared/Util/logger';
import Track                     from '../../../State/Stores/Captions/Track';
import Media                     from '../../../State/Stores/Media/Media';
import SolutionType              from '../Constants/SolutionType';
import SyntheticMediaEventType   from '../Constants/SyntheticMediaEventType';
import IMediaEventsStreamPayload from '../Interfaces/IMediaEventsStreamPayload';
import ISolution                 from '../Interfaces/ISolution';
import ISolutionState            from '../Interfaces/ISolutionState';
import createIsAutoplayRejection from '../Util/createIsAutoplayRejection';
import mapLanguageCodeToLabel    from '../Util/mapLanguageCodeToLabel';
import normaliseLanguageCode     from '../Util/normaliseLanguageCode';

abstract class Html5Base implements ISolution {
    public abstract type: SolutionType;
    public abstract version: string;
    public abstract state: ISolutionState;
    public abstract mediaEvents$: Observable<IMediaEventsStreamPayload>;
    public abstract mediaErrors$: Observable<IErrorsPayload>;
    public abstract videoElement: HTMLVideoElement;
    protected abstract subscriptions: Subscription[];
    protected syntheticMediaEvents$ = new Subject<SyntheticMediaEventType>();
    private isAutoplayRejection = createIsAutoplayRejection();

    public abstract load(media: Media): void;

    public abstract getBitrate(): Promise<number>;

    public abstract setActiveTextTrack(index: number): void;

    public get textTracks(): Track[] {
        const {textTracks} = this.videoElement;

        if (textTracks.length === 1 && textTracks[0].language === '') return [];

        return Array.from(textTracks).map((textTrack, index) => {
            const track = new Track();

            track.index = index;
            track.language = normaliseLanguageCode(textTrack.language);
            track.label = mapLanguageCodeToLabel(track.language);

            return track;
        });
    }

    public play(): void {
        const playPromise = this.videoElement.play();

        if (!playPromise) return;

        playPromise
            .catch((err: Error) => {
                logger.error(err);

                if (this.isAutoplayRejection(err)) {
                    this.syntheticMediaEvents$.next(SyntheticMediaEventType.AUTOPLAY_REJECTION);
                }
            });
    }

    public pause(): void {
        this.videoElement.pause();
    }

    public unload(): void {
        this.pause();

        this.videoElement.removeAttribute('src');
    }

    public seek(nextCurrentTime: number): void {
        this.videoElement.currentTime = nextCurrentTime;
    }

    public volume(nextLevel: number): void {
        this.videoElement.muted = nextLevel === 0;

        this.videoElement.volume = nextLevel;
    }

    public async destroy(): Promise<void> {
        while (this.subscriptions.length > 0) this.subscriptions.pop().unsubscribe();

        this.videoElement.parentElement.removeChild(this.videoElement);
    }

    protected notImplemented(): void {
        throw new Error(`Solution \`${this.type}\` does not implement this method`);
    }
}

export default Html5Base;